import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { Button } from '@lightspeed/flame/Button';
import { FlameTheme } from '@lightspeed/flame/Core';
import { Tooltip } from '@lightspeed/flame/Tooltip';

interface PreauthButtonProps {
  tabIndex?: number;
  disabled?: boolean;
  tooltipText?: string;
}

const TOOLTIP_DELAY_MS = 700;

const PreauthButton: React.FC<PreauthButtonProps> = ({
  tabIndex = 3,
  disabled = true,
  tooltipText = '',
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    // Only show tooltip if there's tooltip text and button is disabled
    if (tooltipText && disabled) {
      timeoutRef.current = window.setTimeout(() => {
        setShowTooltip(true);
      }, TOOLTIP_DELAY_MS);
    }
  };

  const handleMouseLeave = () => {
    // Clear timeout and immediately hide tooltip
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setShowTooltip(false);
  };

  const handleClick = () => {
    // Safely access App global - it may not be available during initial render
    if (window.App?.channel?.vent && window.App?.globalEvents?.register?.PREAUTH_PAY_REQUESTED) {
      window.App.channel.vent.trigger(window.App.globalEvents.register.PREAUTH_PAY_REQUESTED);
    }
    return false;
  };

  const button = (
    <Button
      id="preauthButton"
      data-test="register-preauth-button"
      onClick={handleClick}
      disabled={disabled}
      tabIndex={tabIndex}
      fill
      size="large"
      css={{
        marginTop: '18px',
      }}
    >
      Preauthorization
    </Button>
  );

  // Only show tooltip wrapper if there's tooltip text and button is disabled
  if (tooltipText && disabled) {
    return (
      <FlameTheme>
        <div
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          css={{
            pointerEvents: 'auto',
          }}
        >
          {showTooltip ? <Tooltip content={tooltipText}>{button}</Tooltip> : button}
        </div>
      </FlameTheme>
    );
  }

  return <FlameTheme>{button}</FlameTheme>;
};

// Custom renderer with updateProps support
let currentContainer: HTMLElement | null = null;
let currentProps: PreauthButtonProps = {
  tabIndex: 3,
  disabled: true,
  tooltipText: '',
};

function render(container: HTMLElement, props: PreauthButtonProps) {
  ReactDOM.render(<PreauthButton {...props} />, container);
}

export const PreauthButtonRenderer = {
  mount: () => {
    const container = document.querySelector<HTMLElement>('[data-react-root="PreauthButton"]');
    if (container) {
      currentContainer = container;
      // Get initial props from data attribute if available
      const dataProps = container.getAttribute('data-react-props');
      if (dataProps) {
        try {
          const parsedProps = JSON.parse(dataProps);
          currentProps = { ...currentProps, ...parsedProps };
        } catch (e) {
          // Ignore parse errors
        }
      }
      render(container, currentProps);
    }
  },

  unmount: () => {
    if (currentContainer) {
      ReactDOM.unmountComponentAtNode(currentContainer);
      currentContainer = null;
    }
  },

  updateProps: (newProps: Partial<PreauthButtonProps>) => {
    currentProps = { ...currentProps, ...newProps };
    if (currentContainer) {
      render(currentContainer, currentProps);
    }
  },
};

export default PreauthButton;
